import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import { ApiResponse } from "@/types/global";
import { StudyMaterial } from "@/types/content";
import {
  AddVideoLibraryInputs,
  DisplayConcept,
  DisplayConceptCluster,
  MarkedUserQuestionDisplay,
  UpdateVideoLibraryInputs,
  VideoLibraryDisplay,
  VideoLibraryItem,
} from "@/types/studentDashboard";
import { TagLearnVideoInputs } from "@/types/content";

export default class LearnApi extends ApiVerified {
  async getConceptClustersForChapter(
    userId: number,
    subjectChapterId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<DisplayConceptCluster>>(
        `learn/get-topics`,
        { params: { userId: userId, subjectChapterId: subjectChapterId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getConceptsForConceptCluster(
    subjectChapterId: number,
    topicId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<DisplayConcept>>(`learn/get-concepts`, {
        params: { subjectChapterId: subjectChapterId, topicId: topicId },
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getLearnVideosForChapter(
    subjectChapterId: number,
    onlyActive: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<VideoLibraryDisplay>>(
        `learn/get-videos-for-chapter`,
        { params: { subjectChapterId: subjectChapterId, onlyActive: onlyActive } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Tags are mandatory — the backend seeds the VideoDesign doc (tags + cleaned title,
  // ExtractionStatus=0) and links it to the new row via VideoDesignId.
  async addLearnVideo(inputData: AddVideoLibraryInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`learn/add-video`, inputData);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async searchLearnVideosByName(searchText: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<VideoLibraryItem>>(
        `learn/search-videos-by-name`,
        { params: { searchText: searchText } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async tagLearnVideoToChapter(inputData: TagLearnVideoInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `learn/tag-video-to-chapter`,
        inputData,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateLearnVideo(updatedVideo: UpdateVideoLibraryInputs): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(`learn/update-video`, updatedVideo);
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateLearnVideoTagsForChapter(
    learnVideos: Array<VideoLibraryDisplay>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `learn/update-video-mappings-for-chapter`,
        learnVideos,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // AR-14.2: `userId` is accepted for caller compatibility but no longer sent — the
  // endpoint derives the user from the JWT (docs/api/learn.json get-study-material-for-student).
  async getStudyMaterialForStudent(userId: number, subjectChapterId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<StudyMaterial>>(
        `learn/get-study-material-for-student`,
        { params: { subjectChapterId: subjectChapterId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // AR-14.2: `userId` is accepted for caller compatibility but no longer sent — the
  // endpoint derives the user from the JWT (docs/api/learn.json get-marked-user-questions).
  async getMarkedUserQuestions(
    userId: number,
    courseId: number,
    classId: number,
    subjectId: number,
    markedCategoryId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<MarkedUserQuestionDisplay>>(
        `learn/get-marked-user-questions`,
        {
          params: {
            courseId: courseId,
            classId: classId,
            subjectId: subjectId,
            markedCategoryId: markedCategoryId,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async unmarkUserQuestion(
    userId: number,
    questionId: number,
    markedCategoryId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(`learn/unmark-user-question`, {
        UserId: userId,
        QuestionId: questionId,
        MarkedCategoryId: markedCategoryId,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
